import type { TFunction } from '@course-authoring/i18n';

interface ResultsDistributionIdentity {
  id: 'my-org' | 'global';
  responseCount: number | string;
}

interface ResultsLocalizationKeys {
  myOrgTitle: string;
  globalTitle: string;
  responsesOne: string;
  responsesOther: string;
}

interface LocalizedResultsMetadata {
  title: string;
  responsesLabel: string;
}

// Compact notation abbreviates per the target locale's own conventions (CLDR
// data), e.g. 130,600 -> "130.6K" in English, "130,6 k" in French, "13.1万" in
// Japanese - rather than always dividing by 1,000 and appending a Latin "k".
function formatAbbreviatedResponseCount(count: number, locale: string): string {
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(count);
}

export function localizeResultsDistribution(
  t: TFunction,
  locale: string,
  distribution: ResultsDistributionIdentity,
  keys: ResultsLocalizationKeys,
): LocalizedResultsMetadata {
  const formattedResponseCount = typeof distribution.responseCount === 'number'
    ? formatAbbreviatedResponseCount(distribution.responseCount, locale)
    : distribution.responseCount;
  const responsesKey = distribution.responseCount === 1
    ? keys.responsesOne
    : keys.responsesOther;
  const responsesLabel = t(responsesKey, { responseCount: formattedResponseCount });

  return {
    title: distribution.id === 'my-org' ? t(keys.myOrgTitle) : t(keys.globalTitle),
    responsesLabel,
  };
}
